/**
 * The `punycode/punycode.js` import is needed to avoid importing a node.js punycode module.
 * See https://www.npmjs.com/package/punycode#installation and the "In Node.js section".
 * See https://github.com/mathiasbynens/punycode.js/issues/132 and https://github.com/mathiasbynens/punycode.js/issues/132#issuecomment-1902750632
 */
import { cloneDeep } from "lodash";
import { toASCII } from "punycode/punycode.js";
import { DATA_URL_PATTERN, matchSafeUrl } from "utils/regex";
import { z } from "zod";

export const validateEmail = (value: string): boolean => {
  // Based on Django for compatibility with monolith: https://github.com/django/django/blob/9d756afb07de8ef6e4d1980413979496643f1c3b/django/core/validators.py#L174-L250
  const userRegex =
    /**
     * TypeScript started complaining about this regular expression when upgraded to 5.5.2.
     */
    // @ts-ignore
    /(^[-!#$%&'*+/=?^_`{}|~0-9A-Z]+(\.[-!#$%&'*+/=?^_`{}|~0-9A-Z]+)*$|^\"([\001-\010\013\014\016-\037!#-\[\]-\177]|\\[\001-\011\013\014\016-\177])*\"$)/i;

  if (!value || !value.includes("@")) {
    return false;
  }

  const parts = value.split("@");

  const userPart = parts[0];
  const domainPart = parts[1];

  if (!userRegex.test(userPart)) {
    return false;
  }

  if (!validateDomainPart(domainPart)) {
    const punycodeDomainPart = toASCII(domainPart);
    if (!validateDomainPart(punycodeDomainPart)) {
      return false;
    }
  }

  return true;
};

export const validateDomainPart = (domainPart: string): boolean => {
  // domain name + an arbitrary number of subdomains can be a max length of 63, which
  // includes a leading character and a trailing character that cannot be a dash
  // TLD must be a min length of 2 and a max of 63, but may not end with a dash
  // Examples of invalid domains: -zapier.com, zapier-.com, zapier.com-, zapier.c
  const domainRegex =
    /^((?:[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?\.)+)(?:[A-Z0-9-]{1,62}[A-Z0-9])$/i;

  const literalRegex = /\[([A-F0-9:.]+)\]$/i;
  if (domainRegex.test(domainPart)) {
    return true;
  }

  // This differs from Django in two ways:
  // 1. There is no exact equivalent of re.match in JS: https://docs.python.org/3/library/re.html#re.match
  // 2. We fallback to Zod validation for IP addresses and assume it matches Python core (which is what Django uses)
  const literalMatch = domainPart.match(literalRegex);
  if (literalMatch) {
    const ipAddress = literalMatch[0];
    const parseResult = z.string().ip().safeParse(ipAddress);
    return parseResult.success;
  }
  return false;
};

export const validateLink = (url: string): boolean => {
  // A valid link is either a well formed web address, a relative address (within the same domain),
  // or one of a select few kinds of data addresses.
  if (!url || url === "about:blank") {
    return true;
  }

  url = url.trim();

  const safeUrlMatch = matchSafeUrl(url);
  const dataUrlMatch = url.match(DATA_URL_PATTERN);

  if (safeUrlMatch?.hasDomainWithoutProtocol) {
    // we reject these, because we expect front-end to have
    // already performed transform to add protocol in this case.
    return false;
  }
  return !!(safeUrlMatch.isMatch || dataUrlMatch);
};

/**
 * 1. The OpenAPI schema generated by `zod-openapi` differs between _input_ and _output_ types.
 * 2. Most of our schemas have the `effectType: input` annotation added, but we used it for both _output_ and _input_ schemas.
 * 3. When migrating TRPC to OpenAPI for endpoints like `PATCH` for a given page, the Zod → OpenAPI command threw errors because we were reusing the same schemas for _input_ and _output_.
 * 4. Our schemas are enormous and consist of multiple parts, so duplicating them is not an option.
 * 5. Since duplication is not an option, we had to devise a programmatic way to convert any schema into either an "input" or "output" schema.
 */
export function toOutputSchema<T extends z.ZodTypeAny>(schema: T): T {
  if (!schema._def) {
    return schema;
  }

  if (hasSeenOutputSchema(schema)) {
    return schema;
  }

  const typeName = schema._def.typeName;
  switch (typeName) {
    case "ZodObject": {
      const objectSchema = schema as unknown as z.AnyZodObject;

      let newObject = z.object(
        Object.fromEntries(
          Object.entries(objectSchema.shape).map(([key, value]) => {
            return [key, toOutputSchema(value as z.ZodTypeAny)];
          })
        )
      ) as unknown as T;

      if (objectSchema.description) {
        newObject = newObject.describe(
          objectSchema.description
        ) as unknown as T;
      }

      return newObject.openapi(
        getOutputSchemaOpenApiProps(objectSchema)
      ) as unknown as T;
    }

    case "ZodArray": {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const arraySchema = schema as unknown as z.ZodArray<any>;

      let newArraySchema = z.array(
        toOutputSchema(arraySchema.element)
      ) as unknown as T;

      if (arraySchema.description) {
        newArraySchema = newArraySchema.describe(
          arraySchema.description
        ) as unknown as T;
      }

      return newArraySchema.openapi(
        getOutputSchemaOpenApiProps(arraySchema)
      ) as unknown as T;
    }
    case "ZodEnum": {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const enumSchema = schema as unknown as z.ZodEnum<any>;

      let newEnumSchema = z.enum(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        enumSchema.options.map((option: any) => {
          return toOutputSchema(option);
        })
      );

      if (enumSchema.description) {
        newEnumSchema = newEnumSchema.describe(enumSchema.description);
      }

      return newEnumSchema.openapi(
        getOutputSchemaOpenApiProps(enumSchema)
      ) as unknown as T;
    }

    case "ZodOptional": {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const optionalSchema = schema as unknown as z.ZodOptional<any>;
      const newSchema = toOutputSchema(optionalSchema._def.innerType);

      let newOptionalSchema = newSchema
        .openapi(getOutputSchemaOpenApiProps(optionalSchema))
        .optional()
        .openapi({ effectType: "output" }) as unknown as T;

      if (optionalSchema.description) {
        newOptionalSchema = newOptionalSchema.describe(
          optionalSchema.description
        ) as unknown as T;
      }

      return newOptionalSchema;
    }

    case "ZodDefault": {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const defaultSchema = schema as unknown as z.ZodDefault<any>;

      const newSchema = toOutputSchema(defaultSchema._def.innerType);
      let newDefaultSchema = newSchema
        .openapi(getOutputSchemaOpenApiProps(defaultSchema))
        .default(defaultSchema._def.defaultValue())
        .openapi({ effectType: "output" }) as unknown as T;

      if (defaultSchema.description) {
        newDefaultSchema = newDefaultSchema.describe(
          defaultSchema.description
        ) as unknown as T;
      }

      return newDefaultSchema;
    }

    case "ZodUnion": {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const unionSchema = schema as unknown as z.ZodUnion<any>;

      let newUnionSchema = z.union(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        unionSchema.options.map((option: any) => {
          return toOutputSchema(option);
        })
      );

      if (unionSchema.description) {
        newUnionSchema = newUnionSchema.describe(unionSchema.description);
      }

      return newUnionSchema.openapi(
        getOutputSchemaOpenApiProps(unionSchema)
      ) as unknown as T;
    }

    case "ZodDiscriminatedUnion": {
      const discriminatedUnionSchema =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        schema as unknown as z.ZodDiscriminatedUnion<any, any>;

      let newDiscriminatedUnionSchema = z.discriminatedUnion(
        discriminatedUnionSchema.discriminator,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        discriminatedUnionSchema.options.map((option: any) => {
          return toOutputSchema(option);
        })
      );

      if (discriminatedUnionSchema.description) {
        newDiscriminatedUnionSchema = newDiscriminatedUnionSchema.describe(
          discriminatedUnionSchema.description
        );
      }

      return newDiscriminatedUnionSchema.openapi(
        getOutputSchemaOpenApiProps(discriminatedUnionSchema)
      ) as unknown as T;
    }

    case "ZodLiteral": {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const literalSchema = schema as unknown as z.ZodLiteral<any>;

      let newLiteralSchema = z.literal(literalSchema.value);

      if (literalSchema.description) {
        newLiteralSchema = newLiteralSchema.describe(literalSchema.description);
      }

      return newLiteralSchema.openapi(
        getOutputSchemaOpenApiProps(literalSchema)
      ) as unknown as T;
    }

    case "ZodBoolean": {
      const booleanSchema = schema as unknown as z.ZodBoolean;

      const newBooleanSchema = cloneDeep(booleanSchema);

      return newBooleanSchema.openapi(
        getOutputSchemaOpenApiProps(booleanSchema)
      ) as unknown as T;
    }

    case "ZodNumber": {
      const numberSchema = schema as unknown as z.ZodNumber;

      const newNumberSchema = cloneDeep(numberSchema);

      return newNumberSchema.openapi(
        getOutputSchemaOpenApiProps(numberSchema)
      ) as unknown as T;
    }

    case "ZodString": {
      const stringSchema = schema as unknown as z.ZodString;

      const newStringSchema = cloneDeep(stringSchema);

      return newStringSchema.openapi(
        getOutputSchemaOpenApiProps(stringSchema)
      ) as unknown as T;
    }

    default: {
      return schema.openapi(
        getOutputSchemaOpenApiProps(schema)
      ) as unknown as T;
    }
  }
}

/**
 * 1. The OpenAPI schema generated by `zod-openapi` differs between _input_ and _output_ types.
 * 2. Most of our schemas have the `effectType: output` annotation added, but we used it for both _output_ and _input_ schemas.
 * 3. When migrating TRPC to OpenAPI for endpoints like `PATCH` for a given page, the Zod → OpenAPI command threw errors because we were reusing the same schemas for _input_ and _output_.
 * 4. Our schemas are enormous and consist of multiple parts, so duplicating them is not an option.
 * 5. Since duplication is not an option, we had to devise a programmatic way to convert any schema into either an "input" or "output" schema.
 */

export function toInputSchema<T extends z.ZodTypeAny>(schema: T): T {
  if (!schema._def) {
    return schema;
  }

  if (hasSeenInputSchema(schema)) {
    return schema;
  }

  const typeName = schema._def.typeName;
  switch (typeName) {
    case "ZodObject": {
      const objectSchema = schema as unknown as z.AnyZodObject;

      let newObjectSchema = z.object(
        Object.fromEntries(
          Object.entries(objectSchema.shape).map(([key, value]) => {
            return [key, toInputSchema(value as z.ZodTypeAny)];
          })
        )
      ) as unknown as T;

      if (objectSchema.description) {
        newObjectSchema = newObjectSchema.describe(
          objectSchema.description
        ) as unknown as T;
      }

      return newObjectSchema.openapi(
        getInputSchemaOpenApiProps(objectSchema)
      ) as unknown as T;
    }

    case "ZodArray": {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const arraySchema = schema as unknown as z.ZodArray<any>;

      let newArraySchema = z.array(toInputSchema(arraySchema.element));

      if (arraySchema.description) {
        newArraySchema = newArraySchema.describe(arraySchema.description);
      }

      return newArraySchema.openapi(
        getInputSchemaOpenApiProps(arraySchema)
      ) as unknown as T;
    }
    case "ZodEnum": {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const enumSchema = schema as unknown as z.ZodEnum<any>;

      let newEnumSchema = z.enum(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        enumSchema.options.map((option: any) => {
          return toInputSchema(option);
        })
      );

      if (enumSchema.description) {
        newEnumSchema = newEnumSchema.describe(enumSchema.description);
      }

      return newEnumSchema.openapi(
        getInputSchemaOpenApiProps(enumSchema)
      ) as unknown as T;
    }

    case "ZodOptional": {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const optionalSchema = schema as unknown as z.ZodOptional<any>;
      const newSchema = toInputSchema(optionalSchema._def.innerType);

      let newOptionalSchema = newSchema
        .openapi(getInputSchemaOpenApiProps(optionalSchema))
        .optional()
        .openapi({ effectType: "input" }) as unknown as T;

      if (optionalSchema.description) {
        newOptionalSchema = newOptionalSchema.describe(
          optionalSchema.description
        ) as unknown as T;
      }

      return newOptionalSchema;
    }

    case "ZodDefault": {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const defaultSchema = schema as unknown as z.ZodDefault<any>;
      const newSchema = toInputSchema(defaultSchema._def.innerType);

      let newDefaultSchema = newSchema
        .openapi(getInputSchemaOpenApiProps(defaultSchema))
        .default(defaultSchema._def.defaultValue())
        .openapi({ effectType: "input" }) as unknown as T;

      if (defaultSchema.description) {
        newDefaultSchema = newDefaultSchema.describe(
          defaultSchema.description
        ) as unknown as T;
      }

      return newDefaultSchema;
    }

    case "ZodUnion": {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const unionSchema = schema as unknown as z.ZodUnion<any>;

      let newUnionSchema = z.union(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        unionSchema.options.map((option: any) => {
          return toInputSchema(option);
        })
      );

      if (unionSchema.description) {
        newUnionSchema = newUnionSchema.describe(unionSchema.description);
      }

      return newUnionSchema.openapi(
        getInputSchemaOpenApiProps(unionSchema)
      ) as unknown as T;
    }

    case "ZodDiscriminatedUnion": {
      const discriminatedUnionSchema =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        schema as unknown as z.ZodDiscriminatedUnion<any, any>;

      let newDiscriminatedUnionSchema = z.discriminatedUnion(
        discriminatedUnionSchema.discriminator,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        discriminatedUnionSchema.options.map((option: any) => {
          return toInputSchema(option);
        })
      );

      if (discriminatedUnionSchema.description) {
        newDiscriminatedUnionSchema = newDiscriminatedUnionSchema.describe(
          discriminatedUnionSchema.description
        );
      }

      return newDiscriminatedUnionSchema.openapi(
        getInputSchemaOpenApiProps(discriminatedUnionSchema)
      ) as unknown as T;
    }

    case "ZodLiteral": {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const literalSchema = schema as unknown as z.ZodLiteral<any>;

      let newLiteralSchema = z.literal(literalSchema.value);

      if (literalSchema.description) {
        newLiteralSchema = newLiteralSchema.describe(literalSchema.description);
      }

      return newLiteralSchema.openapi(
        getInputSchemaOpenApiProps(literalSchema)
      ) as unknown as T;
    }

    case "ZodBoolean": {
      const booleanSchema = schema as unknown as z.ZodBoolean;

      const newBooleanSchema = cloneDeep(booleanSchema);

      return newBooleanSchema.openapi(
        getInputSchemaOpenApiProps(booleanSchema)
      ) as unknown as T;
    }

    case "ZodNumber": {
      const numberSchema = schema as unknown as z.ZodNumber;

      const newNumberSchema = cloneDeep(numberSchema);

      return newNumberSchema.openapi(
        getInputSchemaOpenApiProps(numberSchema)
      ) as unknown as T;
    }

    case "ZodString": {
      const stringSchema = schema as unknown as z.ZodString;

      const newStringSchema = cloneDeep(stringSchema);

      return newStringSchema.openapi(
        getInputSchemaOpenApiProps(stringSchema)
      ) as unknown as T;
    }

    default: {
      return schema.openapi(getInputSchemaOpenApiProps(schema)) as unknown as T;
    }
  }
}

const outputSchemaRefs = new Set<string>();

function getOutputSchemaOpenApiProps(schema: z.ZodTypeAny) {
  const existingProps = schema._def.openapi ?? {};
  const ref = existingProps.ref;
  if (!ref) {
    return { ...existingProps, effectType: "output" };
  }

  if (outputSchemaRefs.has(ref)) {
    return { ...existingProps, effectType: "output" };
  }

  const outputSchemaRef = toOutputSchemaRef({ ref });
  outputSchemaRefs.add(outputSchemaRef);

  return { ...existingProps, effectType: "output", ref: outputSchemaRef };
}

function hasSeenOutputSchema(schema: z.ZodTypeAny) {
  const existingProps = schema._def.openapi ?? {};
  const ref = existingProps.ref;
  if (!ref) {
    return false;
  }

  return outputSchemaRefs.has(toOutputSchemaRef({ ref }));
}

function toOutputSchemaRef({ ref }: { ref: string }) {
  if (ref.endsWith("Output")) {
    return ref;
  }

  return `${ref}Output`;
}

const inputSchemaRefs = new Set<string>();

function getInputSchemaOpenApiProps(schema: z.ZodTypeAny) {
  const existingProps = schema._def.openapi ?? {};
  const ref = existingProps.ref;
  if (!ref) {
    return { ...existingProps, effectType: "input" };
  }

  if (inputSchemaRefs.has(ref)) {
    return { ...existingProps, effectType: "input" };
  }

  const inputSchemaRef = toInputSchemaRef({ ref });
  inputSchemaRefs.add(inputSchemaRef);

  return { ...existingProps, effectType: "input", ref: inputSchemaRef };
}

function hasSeenInputSchema(schema: z.ZodTypeAny) {
  const existingProps = schema._def.openapi ?? {};
  const ref = existingProps.ref;
  if (!ref) {
    return false;
  }

  return inputSchemaRefs.has(toInputSchemaRef({ ref }));
}

function toInputSchemaRef({ ref }: { ref: string }) {
  if (ref.endsWith("Input")) {
    return ref;
  }

  return `${ref}Input`;
}
